/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useEffect, useCallback } from 'react';
import { find, orderBy } from 'lodash';
import {
  AFPTable,
  Button,
  Spinner,
  Label,
  EmptyState,
  useModal,
  connectModal,
  PageTitle,
} from '@gsa/afp-component-library';
import { useLazyQuery } from '@apollo/client';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import StandardFieldset from '@/components/forms/standard-fieldset';
import solicitationNotifications from '../utils/solicitation-notification-helpers';
import {
  viewSolicitationReopenPeriodsAtom,
  viewSolicitationDetailAtom,
  viewSolicitationStdItemsAtom,
  viewSolicitationStdItemsCountAtom,
} from '../atoms/view-solicitation-atom';
import {
  GET_SOLICITATION_BY_ID_WITH_CONTRACTS,
  GET_FVS_OPTIONS_FOR_YEAR,
} from '../queries';
import {
  PURCHASE_TYPE_MAP,
  MODAL_MODES,
  SOLICITATION_TYPES,
} from '../constants';
import ViewSolicitationStdItemsModal from './view-solicitation-std-items-modal';
import useSolicitation from '../components/apis/solicitation-apis';
import DeleteSolicitationModal from '../components/delete-solicitation-modal';
import MarkUpText from '../../../components/MarkUpText/MarkUpText';
import useToggle from '../../../hooks/use-toggle';
import {
  processOpenPeriod,
  processReopenPeriods,
} from '../utils/open-reopen-period-util';
import ArchiveSolicitationButton from './archive-solicitation-button';
import ReactivateSolicitationButton from './reactivate-solicitation-button';
import {
  hasContractAwardedForSolicitation,
  hasContractAwardedForNewSolicitationVersion,
} from '../utils/solicitation-table-utils';
import SolicitationPeriodsTableSubComponent from '../components/solicitation-periods-table-sub-comp';
import { isValidUPIID } from '../utils/solicitation-upiid-utils';
import OpenPeriodTable from '../edit-solicitation/6-review/open-period-table';
import { emDashUnicode } from '../../../utilities/constants';
import { ReopenPeriodcolumns } from '../edit-solicitation/6-review/helpers';
import SolicitationStatusBadge from '../components/solicitation-status-badge';
import SolicitationActionButton from './solicitation-action-button';

const ViewSolicitationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [showDeleteSolModal, toggleDeleteSolModal] = useToggle();

  const [order, setOrder] = useState([['period', 'ASC']]);
  const [solicitation, setSolicitation] = useRecoilState(
    viewSolicitationDetailAtom,
  );

  const [openPeriod, setOpenPeriod] = useState({});

  const [reopenPeriods, setReopenPeriods] = useRecoilState(
    viewSolicitationReopenPeriodsAtom,
  );

  const [stdItemSelectionCount, setStdItemSelectionCount] = useRecoilState(
    viewSolicitationStdItemsCountAtom,
  );

  const [standardItems, setStandardItems] = useRecoilState(
    viewSolicitationStdItemsAtom,
  );

  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  const tableRef = React.createRef();
  const { isOpen, openModal, closeModal } = useModal();
  const { setModalMode, setSelectedSolicitationRow, aacCodes } =
    useSolicitation();

  const getReopenPeriods = (periods) => {
    const ropenPeriods =
      periods.length > 1
        ? periods.toSorted(
            (a, b) => new Date(a.startDate) - new Date(b.startDate),
          )
        : periods;
    let reopenCount = 0;
    let midCycleCount = 0;

    return processReopenPeriods(ropenPeriods).map((prd) => {
      return {
        ...prd,
        period: `${prd.periodType === 'R' ? 'Reopen' : 'Mid-cycle'} period ${
          // eslint-disable-next-line no-plusplus
          prd.periodType === 'R' ? ++reopenCount : ++midCycleCount
        }`,
      };
    });
  };

  const getStandardItemCount = (programs) => {
    let count = 0;
    if (programs && programs.length > 0) {
      programs.forEach((program) => {
        count += program.solicitationLines?.length;
      });
    }

    return count;
  };

  const getStandardItems = (programs, vTypes) => {
    const stdItems = [];
    if (programs && programs.length > 0) {
      programs.forEach((program) => {
        program.solicitationLines?.forEach((solctnLine) => {
          const vehicleType = find(vTypes, {
            value: solctnLine?.standardItem?.vehicleType,
          });
          stdItems.push({
            standardItemNumber: solctnLine?.standardItem?.standardItemNumber,
            title: `${solctnLine?.standardItem?.standardItemNumber}-${solctnLine?.standardItem?.title}`,
            vehicleType: vehicleType ? vehicleType.label : emDashUnicode,
            estimatedQty: solctnLine.estimatedQty,
            estimatedFleetQty: solctnLine.estimatedFleetQty,
          });
        });
      });
    }

    return orderBy([...stdItems], 'standardItemNumber', 'asc');
  };

  const [getSolicitationById] = useLazyQuery(
    GET_SOLICITATION_BY_ID_WITH_CONTRACTS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setSolicitation(data.getSolicitationById);
        const reopenPrds = getReopenPeriods([
          ...data.getSolicitationById.solicitationPeriods,
        ]);
        const openPeriodTemp = processOpenPeriod([
          ...data.getSolicitationById.solicitationPeriods,
        ]);
        if (openPeriodTemp?.startDate) {
          setOpenPeriod(openPeriodTemp);
        }
        setReopenPeriods(reopenPrds);
        setStdItemSelectionCount(
          getStandardItemCount([...data.getSolicitationById.programs]),
        );
      },
    },
  );

  const [getFvsCodes] = useLazyQuery(GET_FVS_OPTIONS_FOR_YEAR, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const vehicleTypes = data.getVehicleClassificationFVS.filter(
        (inst) => inst.uniqueKey === 'vehicleType',
      );
      setStandardItems(getStandardItems(solicitation.programs, vehicleTypes));
    },
  });

  const getData = () => {
    void getSolicitationById({
      variables: {
        id: Number.parseInt(id),
      },
    });
  };

  const navigateToListPage = () => {
    navigate(`/catalog/solicitations`);
    // set the toast message before navigate to list page
    const message = `Solicitation ${solicitation.solicitationNumber} has been successfully deleted.`;
    setSolicitationNotification([
      {
        id: 'DELETE_SOLICITATION',
        message: (
          <MarkUpText
            content={message}
            textToMarkUp={`Solicitation ${solicitation.solicitationNumber}`}
            marker="strong"
          />
        ),
        type: 'success',
        closeable: true,
        showInModal: false,
        showOnNextPage: true,
      },
    ]);
  };

  useEffect(() => {
    getData();
  }, [id]);

  const onSort = (newOrder) => {
    setReopenPeriods(
      orderBy(
        [...(reopenPeriods ?? [])],
        newOrder[0]?.[0] ?? '',
        newOrder[0]?.[1]?.toLowerCase() ?? 'asc',
      ),
    );
    setOrder(newOrder);
  };

  useEffect(() => {
    if (solicitation?.contractYear) {
      void getFvsCodes({
        variables: {
          year: solicitation.contractYear,
          includeOnlyCommentable: false,
        },
      });
    }
  }, [solicitation]);

  useEffect(() => {
    // clear notifications
    setSolicitationNotification([]);
  }, []);

  const ConnectedModal = connectModal(() => (
    <ViewSolicitationStdItemsModal
      closeModal={closeModal}
      stdItems={standardItems}
    />
  ));

  const ConnectedDeleteSolModal = connectModal(DeleteSolicitationModal);

  // should have same logic as getActionsForSolicitation() in solicitation-table.jsx
  const renderButtons = useCallback(
    (solicitationStatus, solicitationId) => {
      // No action buttons if solicitation UPIID is invalid
      if (!isValidUPIID(solicitation.solicitationNumber, aacCodes)) return null;

      switch (solicitationStatus) {
        case 'Draft':
        case 'New':
          return (
            <>
              <Button
                variant="secondary"
                label="Delete"
                onClick={() => {
                  setSelectedSolicitationRow(solicitation);
                  // show confirmation message modal, delete operation is invoked on delete button on Modal
                  setModalMode({
                    mode: MODAL_MODES.SOLICITATION_CONFIRM_DELETE,
                    showInModal: true,
                  });
                  toggleDeleteSolModal();
                }}
              />
              <SolicitationActionButton
                action="edit"
                label="Edit"
                solicitationId={solicitationId}
              />
            </>
          );
        case 'Open':
          return (
            <SolicitationActionButton
              action="edit"
              label="Edit"
              solicitationId={solicitationId}
            />
          );
        case 'Closed':
          return (
            <>
              <ArchiveSolicitationButton solicitation={solicitation} />
              {solicitation.solicitationType !==
                SOLICITATION_TYPES.Standalone &&
                !hasContractAwardedForNewSolicitationVersion(solicitation) && (
                  <SolicitationActionButton
                    action="renew"
                    label="Renew"
                    solicitationId={solicitationId}
                  />
                )}
              {hasContractAwardedForSolicitation(solicitation) ? (
                <SolicitationActionButton
                  action="midcycle"
                  label="Mid-cycle"
                  solicitationId={solicitationId}
                />
              ) : (
                <SolicitationActionButton
                  action="reopen"
                  label="Reopen"
                  solicitationId={solicitationId}
                />
              )}
            </>
          );
        case 'Archived':
          return <ReactivateSolicitationButton solicitation={solicitation} />;
        default:
          return null;
      }
    },
    [aacCodes, standardItems, solicitation, navigate],
  );

  return solicitation ? (
    <div className="margin-bottom-8" data-testid="view-solicitation-detail">
      <div className="grid-row margin-bottom-2">
        <PageTitle
          title={`Solicitation - ${solicitation?.solicitationNumber}`}
          aria-label={`Solicitation - ${solicitation?.solicitationNumber}`}
          className="margin-top-0"
          tabIndex="0"
        />
        <div className="statusBadgeContainer margin-left-1 margin-top-2">
          <SolicitationStatusBadge status={solicitation?.status} />
        </div>
      </div>
      <StandardFieldset>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label="Solicitation uPIID Number"
          >
            <strong>
              <span>Solicitation uPIID number</span>
            </strong>
          </div>
        </div>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label={solicitation.solicitationNumber}
          >
            <span>{solicitation.solicitationNumber}</span>
          </div>
        </div>
      </StandardFieldset>
      <StandardFieldset>
        <div className="grid-row grid-gap">
          <div
            className="grid-col-2"
            tabIndex="0"
            aria-label={`Contract Year ${solicitation.contractYear}`}
          >
            <Label>
              <strong>Contract year</strong>
            </Label>
            <span>{solicitation.contractYear}</span>
          </div>
          <div
            className="grid-col-2"
            tabIndex="0"
            aria-label={`Purchase Type ${
              PURCHASE_TYPE_MAP[solicitation.purchaseTypeCode]
            }`}
          >
            <Label>
              <strong>Purchase type</strong>
            </Label>
            <span>{PURCHASE_TYPE_MAP[solicitation.purchaseTypeCode]}</span>
          </div>
          <div
            className="grid-col-2"
            tabIndex="0"
            aria-label={`Solicitation Process ${solicitation.solicitationType}`}
          >
            <Label>
              <strong>Solicitation process</strong>
            </Label>
            <span>{solicitation.solicitationType}</span>
          </div>
        </div>
      </StandardFieldset>
      <StandardFieldset label="Solicitation details" name="solicitationDetails">
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label="Solicitation title"
          >
            <strong>
              <span>Solicitation title</span>
            </strong>
          </div>
        </div>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label={solicitation.title ? solicitation.title : emDashUnicode}
          >
            <span>
              {solicitation.title ? solicitation.title : emDashUnicode}
            </span>
          </div>
        </div>
      </StandardFieldset>
      <StandardFieldset>
        <div className="grid-row">
          <div className="grid-col-8" tabIndex="0" aria-label="description">
            <strong>
              <span>Description</span>
            </strong>
          </div>
        </div>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label={
              solicitation.description
                ? solicitation.description
                : emDashUnicode
            }
          >
            <span>
              {solicitation.description
                ? solicitation.description
                : emDashUnicode}
            </span>
          </div>
        </div>
      </StandardFieldset>
      <StandardFieldset>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label="Solicitation posting"
          >
            <strong>
              <span>Solicitation posting</span>
            </strong>
          </div>
        </div>
        <div className="grid-row">
          <div
            className="grid-col-8"
            tabIndex="0"
            aria-label={
              solicitation.samUrl ? solicitation.samUrl : emDashUnicode
            }
          >
            <span>
              {solicitation.samUrl ? (
                <a href={solicitation.samUrl}>{solicitation.samUrl}</a>
              ) : (
                emDashUnicode
              )}
            </span>
          </div>
        </div>
      </StandardFieldset>
      <StandardFieldset label="Open period" className="margin-bottom-0">
        <OpenPeriodTable openPeriod={openPeriod} />
      </StandardFieldset>
      <StandardFieldset label="Additional periods">
        <div className="grid-col-12">
          <AFPTable
            fullWidth
            testId="reopen-periods-table"
            ref={tableRef}
            columns={ReopenPeriodcolumns}
            data={reopenPeriods}
            onSort={onSort}
            defaultSort={order}
            renderRowSubComponent={SolicitationPeriodsTableSubComponent}
          />
          {reopenPeriods?.length === 0 && (
            <EmptyState
              hasBackground
              containerStyles="padding-y-5 margin-top-1 adding-y-8 margin-top-neg-2"
              topText={<>There is no reopen period for this contract year</>}
            />
          )}
        </div>
      </StandardFieldset>
      <StandardFieldset
        label="Related Standard Items"
        name="relatedStandardItems"
      >
        <div className="grid-row">
          <div
            className="grid-col-8"
            data-testid="view-solicitation-detail-standard-item-count"
            tabIndex="0"
            aria-label={`${stdItemSelectionCount} Standard Items selected`}
          >
            <span>
              <strong>{stdItemSelectionCount}</strong> Standard Items selected
            </span>
          </div>
        </div>
        <div className="grid-row">
          <Button onClick={openModal} variant="unstyled" label="View details" />
          <ConnectedModal isOpen={isOpen} closeModal={closeModal} />
        </div>
      </StandardFieldset>
      <StandardFieldset>
        <div className="grid-row grid-gap-4 margin-top-4">
          <Button
            onClick={() => {
              navigate('/catalog/solicitations');
            }}
            variant="unstyled"
            label="Cancel"
          />
          {renderButtons(solicitation.status, solicitation.id)}
        </div>
      </StandardFieldset>
      {solicitation && (
        <ConnectedDeleteSolModal
          isOpen={showDeleteSolModal}
          onClose={toggleDeleteSolModal}
          onComplete={navigateToListPage}
        />
      )}
    </div>
  ) : (
    <Spinner aria-busy="true" size="small" />
  );
};

export default ViewSolicitationDetail;
